import React, { useEffect, useState } from 'react';
import { FiImage, FiTrash2, FiX } from 'react-icons/fi';
import {
  datetimeLocalValue,
  formatDueLabel,
  secondsFromDateInput,
} from './parse_deadline';
import {
  fetchTodoAttachmentBlob,
  removeTodoAttachment,
  uploadTodoAttachment,
} from './todo_api';

function AttachmentThumb({ file, onRemove }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    fetchTodoAttachmentBlob(file)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.file_id]);

  return (
    <div className="todo-attach-card">
      {url ? (
        <img src={url} alt={file.file_name} />
      ) : (
        <div className="todo-attach-fallback">{file.file_name}</div>
      )}
      <button type="button" className="todo-attach-remove" onClick={() => onRemove(file)} aria-label="Remove photo">
        <FiTrash2 />
      </button>
    </div>
  );
}

export default function TodoItemDetail({ item, onClose, onChange, onDelete, onPatch }) {
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [dueLocal, setDueLocal] = useState(datetimeLocalValue(item.due_at));
  const [reminderLocal, setReminderLocal] = useState(datetimeLocalValue(item.reminder_at));
  const [venueName, setVenueName] = useState(item.venue_name || '');
  const [venueAddress, setVenueAddress] = useState(item.venue_address || '');
  const [urgent, setUrgent] = useState(!!item.urgent);
  const [pinned, setPinned] = useState(!!item.pinned);
  const [files, setFiles] = useState(item.files || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTitle(item.title || '');
    setDescription(item.description || '');
    setDueLocal(datetimeLocalValue(item.due_at));
    setReminderLocal(datetimeLocalValue(item.reminder_at));
    setVenueName(item.venue_name || '');
    setVenueAddress(item.venue_address || '');
    setUrgent(!!item.urgent);
    setPinned(!!item.pinned);
    setFiles(item.files || []);
  }, [item]);

  const saveField = async (patch) => {
    const updated = await onChange({ id: item.id, ...patch });
    return updated;
  };

  const handleDueChange = async (value) => {
    setDueLocal(value);
    const dueAt = secondsFromDateInput(value);
    await saveField({ due_at: dueAt });
  };

  const handleReminderChange = async (value) => {
    setReminderLocal(value);
    await saveField({ reminder_at: secondsFromDateInput(value) });
  };

  const handleFiles = async (list) => {
    for (const file of list) {
      setBusy(true);
      try {
        const confirmed = await uploadTodoAttachment(item.id, file);
        setFiles((prev) => {
          const next = [
            ...prev,
            {
              file_id: confirmed.file_id,
              todo_id: item.id,
              file_name: file.name,
            },
          ];
          if (onPatch) onPatch({ ...item, files: next });
          return next;
        });
      } catch (err) {
        console.error('Todo upload failed', err);
      } finally {
        setBusy(false);
      }
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await removeTodoAttachment(item.id, file.file_id);
      setFiles((prev) => {
        const next = prev.filter((f) => f.file_id !== file.file_id);
        if (onPatch) onPatch({ ...item, files: next });
        return next;
      });
    } catch (err) {
      console.error('Todo remove file failed', err);
    }
  };

  const dueLabel = item.due_at ? formatDueLabel(item.due_at) : null;

  return (
    <div className="todo-modal-backdrop" onClick={onClose} role="presentation">
      <div className="todo-modal todo-modal-wide" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="todo-modal-header">
          <h2>{item.kind === 'folder' ? 'Folder' : 'Task'}</h2>
          <button type="button" className="todo-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <label className="todo-field">
          <span>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const next = title.trim();
              if (next && next !== item.title) saveField({ title: next });
              else setTitle(item.title);
            }}
          />
        </label>

        {item.kind !== 'folder' && (
          <label className="todo-field">
            <span>Notes</span>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => {
                if (description !== (item.description || '')) {
                  saveField({ description });
                }
              }}
              placeholder="Optional details"
            />
          </label>
        )}

        {item.kind !== 'folder' && (
          <label className="todo-field">
            <span>Reminder</span>
            <input type="datetime-local" value={reminderLocal} onChange={(e) => handleReminderChange(e.target.value)} />
            <span className="todo-field-hint">Choose a different reminder time, or clear it for no reminder.</span>
          </label>
        )}

        {item.kind !== 'folder' && (
          <div className="todo-venue-fields">
            <label className="todo-field">
              <span>Venue</span>
              <input
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                onBlur={() => venueName !== (item.venue_name || '') && saveField({ venue_name: venueName })}
                placeholder="Costco"
              />
            </label>
            <label className="todo-field">
              <span>Address</span>
              <input
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
                onBlur={() => venueAddress !== (item.venue_address || '') && saveField({ venue_address: venueAddress })}
                placeholder="Optional address"
              />
            </label>
          </div>
        )}

        {item.kind !== 'folder' && (
          <label className="todo-field">
            <span>Deadline {dueLabel ? `· ${dueLabel}` : ''}</span>
            <input
              type="datetime-local"
              value={dueLocal}
              onChange={(e) => handleDueChange(e.target.value)}
            />
            <span className="todo-field-hint">An enabled reminder keeps its offset when the deadline changes. A cleared reminder stays off.</span>
          </label>
        )}

        {item.kind !== 'folder' && (
          <div className="todo-toggle-row">
            <label>
              <input
                type="checkbox"
                checked={urgent}
                onChange={(e) => {
                  setUrgent(e.target.checked);
                  saveField({ urgent: e.target.checked });
                }}
              />
              Urgent
            </label>
            <label>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => {
                  setPinned(e.target.checked);
                  saveField({ pinned: e.target.checked });
                }}
              />
              Pinned
            </label>
          </div>
        )}

        {item.kind !== 'folder' && (
          <div className="todo-field">
            <span>Photos</span>
            <div className="todo-attach-grid">
              {files.map((file) => (
                <AttachmentThumb key={file.file_id} file={file} onRemove={handleRemoveFile} />
              ))}
              <label className={`todo-attach-add${busy ? ' is-busy' : ''}`}>
                <FiImage />
                <span>{busy ? 'Uploading…' : 'Add photo'}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    handleFiles(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>
        )}

        <div className="todo-modal-actions">
          {(item.parent_id != null || item.can_manage_sharing) ? (
            <button
              type="button"
              className="todo-btn todo-btn-danger"
              onClick={() => onDelete(item)}
            >
              <FiTrash2 /> Delete
            </button>
          ) : <span />}
          <button type="button" className="todo-btn todo-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
